// strings.js — UI text for the Korean/English toggle (backlog #6).
// Keys are grouped by page. Product NAMES are never translated — they're the
// bilingual QB description strings and display as-is in both modes (settled
// decision); search also works in both languages regardless of UI mode.
// Korean copy is business-casual (해요체 where speaking to the user).

export const STRINGS = {
  en: {
    // --- shared / navbar ---
    appName: "Sunjin Trading",
    navCatalog: "Catalog",
    navMyOrders: "My Orders",
    navPresentation: "Presentation Sheet",
    navBarcodes: "Barcode Mapping",
    navOrders: "Orders",
    navCustomers: "Customers",
    navAnalytics: "Analytics",
    navApprovals: "Approvals",
    navProducts: "Products",
    navPromotions: "Promotions",
    navPricing: "Special Pricing",
    navInventory: "Update Inventory",
    navSecOrdering: "Ordering",
    navSecOps: "Daily operations",
    navSecManage: "Management",
    logOut: "Log Out",
    loading: "Loading...",

    // --- login / register ---
    signInTitle: "Sunjin Trading — Sign In",
    email: "Email",
    password: "Password",
    signIn: "Sign In",
    forgotPassword: "Forgot password?",
    sending: "Sending...",
    requestAccess: "Request customer access",
    pendingApproval: "Your account is still pending approval.",
    accountRejected: "This account has not been approved for access.",
    invalidLogin: "Invalid email or password.",
    typeEmailFirst: "Type your email above first, then tap \"Forgot password?\" again.",
    resetSent: (email) => `If an account exists for ${email}, a password reset email is on its way. Check spam too.`,
    createAccount: "Create Account",
    companyName: "Company Name",
    contactName: "Contact Name",
    phoneNumber: "Phone Number",
    requestAccessBtn: "Request Access",
    requestReceived: "Request Received",
    requestReceivedBody: "Your account is pending approval. You'll be able to log in once Sunjin Trading approves your access.",

    // --- catalog ---
    searchPlaceholder: "Search by name, brand, or SKU...",
    allBrands: "All Brands",
    scanBarcode: "📷 Scan Barcode",
    allItems: "All items",
    promotionsChip: (n) => `🔥 Promotions (${n})`,
    newItemsChip: (n) => `✨ New Items (${n})`,
    itemsWithQty: (n) => `${n} item${n !== 1 ? "s" : ""} with quantities set`,
    addAllToCart: (n) => `Add ${n} item${n !== 1 ? "s" : ""} to cart`,
    reviewQueued: "👁 Review",
    queuedItemsTitle: (n) => `Queued items (${n})`,
    closeMatches: "No exact match — showing close matches",
    updateReady: "⬆ A new version is ready — tap to update",
    viewPhotos: "🖼 Photos",
    viewList: "📋 List",
    clearCart: "🗑 Remove all items",
    sortOriginal: "✍ As written",
    sortPacking: "📦 Packing order",
    confirmClearCart: (n) => `Remove ALL ${n} item${n !== 1 ? "s" : ""} from this order?\n\nThe customer selection and note are kept.`,
    inStock: (n) => `${n} in stock`,
    outOfStock: "Out of stock",
    backorderNote: (n) => `${n} in stock — extra will be backordered`,
    oosNote: "Out of stock — this will be backordered",
    shortDate: (d) => `⏳ Short date — best before ${d}`,
    addToOrder: "Add to Order",
    tapPlusFirst: "Tap + to choose a quantity first",
    added: (qty, name) => `Added ${qty} × ${name}`,
    addedName: (name) => `Added ${name}`,
    addedAll: (n) => `Added ${n} item${n !== 1 ? "s" : ""} to cart`,
    loadingCatalog: "Loading catalog...",
    noBarcodeMatch: (code) => `No product mapped to barcode ${code} yet — an admin can map it under Barcode Mapping.`,
    barcodeLookupFailed: (msg) => `Barcode lookup failed: ${msg}`,
    sku: "SKU",
    barcode: "Barcode",
    newBadge: "NEW",
    newPopupTitle: "✨ New items just arrived!",
    newPopupView: "View new items",
    newPopupClose: "Close",
    noPhoto: "No photo yet",

    // --- product modal ---
    perBox: "CAD / box",
    boxes: "Boxes",
    needPartial: "Need a partial box?",
    partialLabel: "Partial box (added to boxes above)",
    partialNone: "None",
    quarterBox: "1/4 box",
    halfBox: "1/2 box",
    threeQuarterBox: "3/4 box",
    totalBoxes: (n, plural) => `Total: ${n} box${plural ? "es" : ""}`,

    // --- scanner / map barcode ---
    scanTitle: "Scan Barcode",
    scanHint: "Point your camera at the product barcode",
    cameraError: (e) => `Could not start camera: ${e}`,
    cameraDenied: "Camera access is blocked for this site.\n\nTo turn it on: tap the icon next to the web address (🔒 or ⚙) → Permissions → Camera → Allow, then open the scanner again.\n\nIf there is no Camera entry there: phone Settings → Apps → Chrome → Permissions → Camera → Allow.",
    unknownBarcode: "Unknown barcode",
    scanned: "Scanned:",
    unknownBarcodeHelp: "This barcode isn't linked to any product yet. Search your item list below and tap the product it belongs to.",
    searchNameSku: "Search by name or SKU...",
    noProductsMatch: "No products match that search.",
    hasBarcode: "has barcode",
    loadingProducts: "Loading products...",
    mappedTo: (name) => `Barcode mapped to ${name}`,

    // --- cart ---
    yourOrder: "Your Order",
    cartEmpty: "Your cart is empty.",
    orderingFor: "Ordering for:",
    change: "Change",
    loadingCustomers: "Loading customers...",
    searchCustomers: "Search customers by name, QB name, or phone...",
    selectCustomer: (n) => `— Select customer (${n}) —`,
    myOwnAccount: "My own account (test order)",
    noCustomersMatch: "No customers match that search.",
    noCustomerRecords: "No customer records yet — add them under Admin → Customers.",
    addItems: "Add items",
    addItemsLabel: "Add items (search by name, SKU, or brand):",
    addItemsPlaceholder: "Type a product name or SKU — Korean or English...",
    emptyOrderHint: "No items in this order yet — search above to add products.",
    promoTag: "(promo)",
    add: "Add",
    product: "Product",
    qty: "Qty",
    price: "Price",
    subtotal: "Subtotal",
    remove: "Remove",
    total: (amt) => `Total: $${amt} CAD`,
    viewOrderSheet: "📋 View order sheet",
    lineNotePlaceholder: "Item note — e.g. store price $19.99, offer at $21 (internal + packing sheet only)",
    noteLabel: "Note for Sunjin (optional)",
    notePlaceholder: "e.g. deliver after 2pm, call on arrival, substitute OK...",
    placeOrder: "Place Order",
    submitting: "Submitting...",
    addItemFirst: "Add at least one item before placing the order.",
    selectCustomerFirst: "Select who this order is for before placing it.",
    orderFailed: (msg) => `Order failed: ${msg} — you can safely press Place Order again.`,
    orderPlaced: "Order Placed",
    orderNumber: "Order number:",
    placedFor: "Placed for:",
    invoiceFollowUp: "We'll follow up with your invoice shortly.",

    // --- my orders ---
    myOrdersTitle: "My Orders",
    noOrdersYet: "You haven't placed any orders yet.",
    loadingOrders: "Loading your orders...",
    orderCol: "Order #",
    date: "Date",
    items: "Items",
    status: "Status",
    processing: "Processing",
    invoiced: "Invoiced",
    cancelled: "Cancelled",
    reorder: "Reorder",
    reorderHint: "Load this order's items into your cart",
    edit: "Edit",
    cancel: "Cancel",
    yourNote: "Your note:",
    ordersHelpNote: "Orders can be edited or cancelled until they've been invoiced. After that, contact Sunjin Trading for changes.",
    syncLockMsg: "This order is being sent to QuickBooks and can no longer be changed here. Please contact Sunjin Trading.",
    confirmCancel: (num) => `Cancel order ${num}? The items will return to stock.`,
    confirmEdit: (num) => `Edit order ${num}? The order will be cancelled and its items placed back in your cart, where you can change them and submit again.`,
    noneAvailable: "None of the items from that order are available right now.",
    someSkipped: (n) => `${n} item${n !== 1 ? "s" : ""} from that order ${n !== 1 ? "are" : "is"} no longer available and was left out.`,
  },

  ko: {
    // --- shared / navbar ---
    appName: "선진무역",
    navCatalog: "상품 목록",
    navMyOrders: "주문 내역",
    navPresentation: "제품 소개서",
    navBarcodes: "바코드 관리",
    navOrders: "주문 관리",
    navCustomers: "거래처 관리",
    navAnalytics: "판매 분석",
    navApprovals: "가입 승인",
    navProducts: "제품 관리",
    navPromotions: "프로모션",
    navPricing: "특별 단가",
    navInventory: "재고 업데이트",
    navSecOrdering: "주문",
    navSecOps: "업무",
    navSecManage: "관리",
    logOut: "로그아웃",
    loading: "불러오는 중...",

    // --- login / register ---
    signInTitle: "선진무역 — 로그인",
    email: "이메일",
    password: "비밀번호",
    signIn: "로그인",
    forgotPassword: "비밀번호를 잊으셨나요?",
    sending: "전송 중...",
    requestAccess: "거래처 계정 신청",
    pendingApproval: "계정이 아직 승인 대기 중입니다.",
    accountRejected: "이 계정은 승인되지 않았습니다.",
    invalidLogin: "이메일 또는 비밀번호가 올바르지 않습니다.",
    typeEmailFirst: "위에 이메일을 먼저 입력한 후 \"비밀번호를 잊으셨나요?\"를 다시 눌러주세요.",
    resetSent: (email) => `${email} 계정이 존재하면 비밀번호 재설정 이메일이 발송됩니다. 스팸함도 확인해 주세요.`,
    createAccount: "계정 만들기",
    companyName: "업체명",
    contactName: "담당자 이름",
    phoneNumber: "전화번호",
    requestAccessBtn: "가입 신청",
    requestReceived: "신청 완료",
    requestReceivedBody: "계정이 승인 대기 중입니다. 선진무역이 승인하면 로그인하실 수 있습니다.",

    // --- catalog ---
    searchPlaceholder: "상품명, 브랜드, 품번으로 검색...",
    allBrands: "전체 브랜드",
    scanBarcode: "📷 바코드 스캔",
    allItems: "전체 상품",
    promotionsChip: (n) => `🔥 프로모션 (${n})`,
    newItemsChip: (n) => `✨ 신상품 (${n})`,
    itemsWithQty: (n) => `${n}개 상품 수량 선택됨`,
    addAllToCart: (n) => `${n}개 상품 장바구니에 담기`,
    reviewQueued: "👁 확인",
    queuedItemsTitle: (n) => `담을 상품 (${n}개)`,
    closeMatches: "정확히 일치하는 상품이 없어 비슷한 결과를 표시합니다",
    updateReady: "⬆ 새 버전이 준비됨 — 탭하여 업데이트",
    viewPhotos: "🖼 사진",
    viewList: "📋 목록",
    clearCart: "🗑 전체 삭제",
    sortOriginal: "✍ 입력 순서",
    sortPacking: "📦 포장 순서",
    confirmClearCart: (n) => `주문의 ${n}개 상품을 모두 삭제할까요?\n\n거래처 선택과 메모는 유지됩니다.`,
    inStock: (n) => `재고 ${n}`,
    outOfStock: "품절",
    backorderNote: (n) => `재고 ${n} — 초과분은 이월주문 처리됩니다`,
    oosNote: "품절 — 이월주문으로 처리됩니다",
    shortDate: (d) => `⏳ 유통기한 임박 — ${d}까지`,
    addToOrder: "주문에 추가",
    tapPlusFirst: "+ 를 눌러 수량을 먼저 선택하세요",
    added: (qty, name) => `${name} ${qty}개 담았습니다`,
    addedName: (name) => `${name} 담았습니다`,
    addedAll: (n) => `${n}개 상품을 장바구니에 담았습니다`,
    loadingCatalog: "상품 목록 불러오는 중...",
    noBarcodeMatch: (code) => `바코드 ${code}에 연결된 상품이 아직 없습니다 — 관리자가 바코드 관리에서 등록할 수 있습니다.`,
    barcodeLookupFailed: (msg) => `바코드 조회 실패: ${msg}`,
    sku: "품번",
    barcode: "바코드",
    newBadge: "NEW",
    newPopupTitle: "✨ 신상품이 입고되었습니다!",
    newPopupView: "신상품 보러가기",
    newPopupClose: "닫기",
    noPhoto: "사진 준비 중",

    // --- product modal ---
    perBox: "CAD / 박스",
    boxes: "박스",
    needPartial: "박스 일부만 필요하세요?",
    partialLabel: "부분 박스 (위 박스 수에 추가됩니다)",
    partialNone: "없음",
    quarterBox: "1/4 박스",
    halfBox: "1/2 박스",
    threeQuarterBox: "3/4 박스",
    totalBoxes: (n) => `합계: ${n} 박스`,

    // --- scanner / map barcode ---
    scanTitle: "바코드 스캔",
    scanHint: "카메라를 상품 바코드에 맞춰주세요",
    cameraError: (e) => `카메라를 시작할 수 없습니다: ${e}`,
    cameraDenied: "이 사이트의 카메라 사용이 차단되어 있습니다.\n\n해제 방법: 주소창 옆 아이콘(🔒 또는 ⚙)을 누른 뒤 권한 → 카메라 → 허용을 선택하고, 스캐너를 다시 열어 주세요.\n\n카메라 항목이 안 보이면: 휴대폰 설정 → 애플리케이션 → Chrome → 권한 → 카메라 → 허용.",
    unknownBarcode: "등록되지 않은 바코드",
    scanned: "스캔된 바코드:",
    unknownBarcodeHelp: "이 바코드는 아직 어떤 상품에도 연결되어 있지 않습니다. 아래에서 상품을 검색해 해당 상품을 선택하세요.",
    searchNameSku: "상품명 또는 품번으로 검색...",
    noProductsMatch: "검색 결과가 없습니다.",
    hasBarcode: "바코드 있음",
    loadingProducts: "상품 불러오는 중...",
    mappedTo: (name) => `바코드가 ${name}에 등록되었습니다`,

    // --- cart ---
    yourOrder: "주문서",
    cartEmpty: "장바구니가 비어 있습니다.",
    orderingFor: "주문 거래처:",
    change: "변경",
    loadingCustomers: "거래처 불러오는 중...",
    searchCustomers: "거래처명, QB명, 전화번호로 검색...",
    selectCustomer: (n) => `— 거래처 선택 (${n}) —`,
    myOwnAccount: "내 계정 (테스트 주문)",
    noCustomersMatch: "검색과 일치하는 거래처가 없습니다.",
    noCustomerRecords: "등록된 거래처가 없습니다 — 관리자 → 거래처 관리에서 추가하세요.",
    addItems: "상품 추가",
    addItemsLabel: "상품 추가 (상품명, 품번, 브랜드로 검색):",
    addItemsPlaceholder: "상품명 또는 품번 입력 — 한글/English...",
    emptyOrderHint: "아직 담긴 상품이 없습니다 — 위에서 검색해 추가하세요.",
    promoTag: "(프로모션)",
    add: "추가",
    product: "상품",
    qty: "수량",
    price: "단가",
    subtotal: "금액",
    remove: "삭제",
    total: (amt) => `합계: $${amt} CAD`,
    viewOrderSheet: "📋 주문서 보기",
    lineNotePlaceholder: "품목 메모 — 예: 매장가 $19.99, $21에 제안 (내부용 + 패킹시트에만 표시)",
    noteLabel: "선진무역에 남길 메모 (선택)",
    notePlaceholder: "예: 오후 2시 이후 배송, 도착 시 전화, 대체 상품 가능...",
    placeOrder: "주문하기",
    submitting: "주문 처리 중...",
    addItemFirst: "주문 전에 상품을 하나 이상 담아주세요.",
    selectCustomerFirst: "주문할 거래처를 먼저 선택해주세요.",
    orderFailed: (msg) => `주문 실패: ${msg} — 주문하기를 다시 눌러도 안전합니다.`,
    orderPlaced: "주문 완료",
    orderNumber: "주문 번호:",
    placedFor: "주문 거래처:",
    invoiceFollowUp: "인보이스는 곧 보내드리겠습니다.",

    // --- my orders ---
    myOrdersTitle: "주문 내역",
    noOrdersYet: "아직 주문 내역이 없습니다.",
    loadingOrders: "주문 내역 불러오는 중...",
    orderCol: "주문 번호",
    date: "날짜",
    items: "품목 수",
    status: "상태",
    processing: "처리 중",
    invoiced: "인보이스 발행됨",
    cancelled: "취소됨",
    reorder: "재주문",
    reorderHint: "이 주문의 상품을 장바구니에 다시 담습니다",
    edit: "수정",
    cancel: "취소",
    yourNote: "남긴 메모:",
    ordersHelpNote: "인보이스 발행 전까지 주문을 수정하거나 취소할 수 있습니다. 이후 변경은 선진무역으로 연락해 주세요.",
    syncLockMsg: "이 주문은 QuickBooks로 전송 중이라 여기서 변경할 수 없습니다. 선진무역으로 연락해 주세요.",
    confirmCancel: (num) => `주문 ${num}을(를) 취소할까요? 상품은 재고로 반환됩니다.`,
    confirmEdit: (num) => `주문 ${num}을(를) 수정할까요? 주문이 취소되고 상품이 장바구니로 돌아가 수정 후 다시 제출할 수 있습니다.`,
    noneAvailable: "해당 주문의 상품 중 현재 구매 가능한 상품이 없습니다.",
    someSkipped: (n) => `해당 주문 중 ${n}개 상품은 더 이상 판매하지 않아 제외되었습니다.`,
  },
};
